import { useEffect, useState } from 'react';

const DEFAULT_HEADER_HEIGHT = 56;
// Rows never get shorter than the grid's standard density, only taller.
const MIN_ROW_HEIGHT = 52;
// Breathing room above and below the wrapped title, so the lines don't touch the borders.
const VERTICAL_PADDING = 12;
const TITLE = '.MuiDataGrid-columnHeaderTitle';
// Columns whose sort arrow only appears on hover.
const HOVER_ONLY_SORT =
    '.MuiDataGrid-columnHeader--sortable:not(.MuiDataGrid-columnHeader--sorted):not(.MuiDataGrid-columnHeader--filtered)';

/**
 * Headers and cells that wrap instead of truncating with "...", the way Excel does: narrowing a
 * column breaks its text onto more lines - at spaces first, then inside a word when one word
 * alone no longer fits - and the header row / each row grows to fit.
 *
 * Rows the grid sizes itself (`getRowHeight: 'auto'`). The header only takes a fixed height, so
 * this measures the titles whenever one changes size (column resized, window resized, columns
 * re-rendered) and feeds the result back in.
 *
 * Pass `{ cells: false }` for a grid using `rowSpanning`: a spanned cell is sized as row height
 * times the span, which only works while every row is the same height.
 *
 *   const wrapping = useWrappingGrid();
 *   <DataGridPro {...wrapping.gridProps} ... />
 */
export const useWrappingGrid = ({ cells = true }: { cells?: boolean } = {}) => {
    const [grid, setGrid] = useState<HTMLDivElement | null>(null);
    const [columnHeaderHeight, setColumnHeaderHeight] = useState(DEFAULT_HEADER_HEIGHT);

    useEffect(() => {
        if (!grid) return undefined;
        let frame = 0;

        const measure = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
                let tallest = 0;
                grid.querySelectorAll<HTMLElement>(TITLE).forEach((title) => {
                    tallest = Math.max(tallest, title.scrollHeight);
                });
                const next = Math.max(DEFAULT_HEADER_HEIGHT, Math.ceil(tallest) + VERTICAL_PADDING);
                setColumnHeaderHeight((current) => (current === next ? current : next));
            });
        };

        // A title's box changes when its column is resized and when it wraps onto more lines.
        const resizes = new ResizeObserver(measure);
        const observeTitles = () => {
            resizes.disconnect();
            grid.querySelectorAll(TITLE).forEach((title) => resizes.observe(title));
            measure();
        };
        // Header cells are replaced when columns are shown, hidden or reordered. Watching only
        // the header row keeps row re-renders (scrolling, paging) from triggering a re-scan.
        const mutations = new MutationObserver(observeTitles);
        mutations.observe(grid.querySelector('.MuiDataGrid-columnHeaders') || grid, { childList: true, subtree: true });
        observeTitles();

        return () => {
            cancelAnimationFrame(frame);
            resizes.disconnect();
            mutations.disconnect();
        };
    }, [grid]);

    return {
        gridProps: {
            ref: setGrid,
            columnHeaderHeight,
            ...(cells && { getRowHeight: autoRowHeight, getEstimatedRowHeight: estimatedRowHeight }),
            sx: cells ? { ...cellSx, ...headerSx } : headerSx,
        },
    };
};

const autoRowHeight = () => 'auto' as const;
const estimatedRowHeight = () => MIN_ROW_HEIGHT;

// The grid already lets text wrap in auto-height rows; this breaks inside words too, keeps the
// standard row height as a floor and centres content vertically like a fixed-height row.
const cellSx = {
    '& .MuiDataGrid-row--dynamicHeight > .MuiDataGrid-cell': {
        display: 'flex',
        alignItems: 'center',
        minHeight: MIN_ROW_HEIGHT,
        paddingTop: '6px',
        paddingBottom: '6px',
        overflowWrap: 'anywhere',
        lineHeight: 1.3,
    },
};

const headerSx = {
    '& .MuiDataGrid-columnHeaderTitle': {
        whiteSpace: 'normal',
        overflowWrap: 'anywhere',
        textOverflow: 'clip',
        lineHeight: 1.25,
    },
    // The grid reveals the column menu and sort icons on hover by giving them width, which
    // takes it from the title - a narrow title then re-wraps letter by letter and the header
    // jumps. Instead they float in a layer over the right end of the title, which leaves
    // the title's width alone. The menu icon carries one backdrop (fading in from the
    // left) wide enough to sit under the sort arrow too, so no text shows between them.
    // Sorted or filtered columns show their icons all the time, so those stay inline.
    // Touch devices show the icons permanently, so they keep the grid's own layout.
    '@media (hover: hover)': {
        '& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon': {
            position: 'absolute',
            top: 0,
            bottom: 0,
            right: 2,
            marginRight: 0,
            alignItems: 'center',
            paddingLeft: '8px',
            background:
                'linear-gradient(to right, transparent, var(--DataGrid-t-header-background-base) 8px)',
        },
        [`& ${HOVER_ONLY_SORT} .MuiDataGrid-menuIcon`]: { paddingLeft: '40px' },
        [`& ${HOVER_ONLY_SORT} .MuiDataGrid-iconButtonContainer`]: {
            position: 'absolute',
            top: 0,
            bottom: 0,
            right: 32,
            alignItems: 'center',
            zIndex: 1,
        },
    },
};
